import { Fragment, useContext } from 'react'

import { useFormikContext } from 'formik'
import { AppQuizUsageQuestion, QuizContentSubtype } from 'globalTypes'
import { get, isEqual, isNil, size } from 'lodash'

import { useMutation } from '@apollo/client'
import Button from 'Root/App/Button'
import { Context as StateContext } from 'Root/App/Quiz/StateContext'
import {
  getCurrentQuestionIndex,
  getNextQuestion,
  getPreviousQuestion,
  hasPreviousQuestion,
  hasNextQuestion,
} from 'Root/App/Quiz/utilities'

import MUTATION from '../app-quiz-answer-question.graphql'
import styles from './index.module.css'

interface Props {
  question: AppQuizUsageQuestion
}

const Navigation = ({ question }: Props) => {
  const { values, getFieldMeta } = useFormikContext()
  const { state, dispatch } = useContext(StateContext)

  const [mutate] = useMutation(MUTATION)

  // navigation is based on:
  //  - current question validation
  //  - creator allowed to enqueue required questions
  //  - current question to be skippable or not
  //  - current queston has a valid answer
  //  - creator allowed navigation
  //
  // possibilities are:
  //  - previous
  //  - confirm
  //  - skip / next

  const temporaryAnswer = get(values, ['answers', question.id])
  const fieldMeta = getFieldMeta(`answers.${question.id}`)

  const handleConfirm = () => {
    dispatch({
      type: 'answer',
      questionId: question.id,
      data: temporaryAnswer,
    })

    mutate({
      variables: {
        id: state.content.id,
        questionIdentifier: question.id,
        input: JSON.stringify(state.answers),
      },
    })
  }

  const currentQuestionIndex = getCurrentQuestionIndex(state, question.id)
  const isFirstStep = currentQuestionIndex === 0
  const isLastStep = currentQuestionIndex === size(state.questions) - 1

  const handlePrev = () => {
    if (hasPreviousQuestion(state, question.id)) {
      const previousQuestionId = getPreviousQuestion(state, question.id).id

      dispatch({
        type: 'go-to-question',
        questionId: previousQuestionId,
      })
    }
  }

  const handleNext = () => {
    if (hasNextQuestion(state, question.id)) {
      const nextQuestionId = getNextQuestion(state, question.id).id

      dispatch({
        type: 'go-to-question',
        questionId: nextQuestionId,
      })

      return
    }

    dispatch({
      type: 'complete',
    })
  }

  const showPrevious = !isFirstStep && state.content.questionsNavigation
  const nextQuestion = getNextQuestion(state, question.id)?.id

  const showSkip =
    !isLastStep &&
    !isNil(nextQuestion) &&
    nextQuestion !== question.id &&
    state.content.quizSubtype !== QuizContentSubtype.quick_quiz

  // confirm is always displayed
  // but can be disabled if no changes where made on current question
  const canConfirm =
    fieldMeta.touched &&
    !isEqual(get(state, ['answers', question.id]), temporaryAnswer)

  const wasConfirmed =
    !isNil(temporaryAnswer) &&
    isEqual(get(state, ['answers', question.id]), temporaryAnswer)

  const showNext =
    (state.content.quizSubtype !== QuizContentSubtype.quick_quiz &&
      !canConfirm) ||
    (state.content.quizSubtype === QuizContentSubtype.quick_quiz &&
      wasConfirmed)

  return (
    <div className="flex flex-row gap-x-1">
      {showPrevious && (
        <Button className={styles.secondary} onClick={handlePrev}>
          Indietro
        </Button>
      )}
      <Button className="flex-1" disabled={!canConfirm} onClick={handleConfirm}>
        Conferma
      </Button>
      {showNext ? (
        <Button className={styles.secondary} onClick={handleNext}>
          Prossima
        </Button>
      ) : (
        <Fragment>
          {showSkip && (
            <Button className={styles.secondary} onClick={handleNext}>
              Salta
            </Button>
          )}
        </Fragment>
      )}
    </div>
  )
}

export default Navigation
